import { ApiRecord } from '~/types/records/ApiRecord'
import { ApiCollection } from '~/types/records/ApiCollection'
import { RecordType } from '~/types/records/RecordType'

export interface Params {
  [key: string]: string
}

export interface FetchOptions {
  method?: 'GET'|'POST'|'PUT'|'DELETE'|'HEAD'|'OPTIONS'|'PATCH',
  mode?: 'cors'|'no-cors'|'same-origin',
  cache?: 'default'|'no-store'|'reload'|'no-cache'|'force-cache'|'only-if-cached',
  credentials?: 'omit'|'same-origin'|'include',
  headers?: Headers,
  redirect?: 'follow'|'manual'|'error',
  referrerPolicy?: 'no-referrer'|'no-referrer-when-downgrade'|'origin'|'origin-when-cross-origin'|'same-origin'|'strict-origin'|'strict-origin-when-cross-origin'|'unsafe-url',
  body? : string | FormData | URLSearchParams | ReadableStream<Uint8Array> | null | undefined,
}

type JSONResponse = {
  data?: ApiRecord | ApiCollection<BaseRecord>,
  errors?: Array<{message: string}>
}

type ApiPromise = Promise<ApiRecord | ApiCollection<BaseRecord>>

export default class GarbanzoApi {
  baseUrl: string
  static cache: Map<string, ApiPromise> = new Map()
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }
  /**
   * Make a request to the Garbanzo API
   *
   * @param path
   * @param params
   * @param options
   */
  async request(path: string, params: Params = {}, options: FetchOptions = {}): ApiPromise {
    const url = new URL(this.baseUrl + path)
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]))

    const response = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'Accept': 'application/json',
      },
      ...options,
    })

    const { data, errors }: JSONResponse = await response.json()

    if (response.ok) {
      return {
        ...data,
      } as ApiRecord | ApiCollection<BaseRecord>
    } else {
      throw new Error(errors?.map(e => e.message).join('\n') ?? 'unknown')
    }
  }

  /**
   * Get a record by type and ID
   * @param type
   * @param uuid
   * @param skipCache
   */
  getByType(type: RecordType, uuid: string, skipCache  = false): ApiPromise {
    const path = `/${type.toLowerCase()}s/${uuid}`

    if (!skipCache && this.getCache(path)) {
      return this.getCache(path) as ApiPromise
    }

    const response = this.request(path)
    this.setCache(path, response)
    return response
  }

  /**
   * Get lessons related to a lesson by uuid
   * @param uuid
   * @param skipCache
   */
  getRelatedLessons(uuid: string, skipCache = false): ApiPromise {
    const key = `lessons/related/${uuid}`

    if (!skipCache && this.getCache(key)) {
      return this.getCache(key) as ApiPromise
    }

    const response = this.request(`/lessons/related`, {}, {
      method: 'POST',
      body: JSON.stringify({ uuid }),
    })

    this.setCache(key, response)

    return response
  }

  private setCache(id: string, data: ApiPromise) {
    GarbanzoApi.cache.set(id, data)
  }
  private getCache(id: string) {
    return GarbanzoApi.cache.get(id) ?? null
  }
}
